/* eslint-disable react/jsx-no-bind */
import {
  PeopleOutlined as PeopleOutlinedIcon,
  ContactsOutlined as ContactsOutlinedIcon,
  ReceiptOutlined as ReceiptOutlinedIcon,
  ScaleOutlined as ScaleOutlinedIcon,
  InventoryOutlined as InventoryOutlinedIcon,
  MenuOutlined as MenuOutlinedIcon,
  DashboardOutlined as DashboardOutlinedIcon,
  BookOnlineOutlined as BookOnlineOutlinedIcon,
  LocalShippingOutlined as LocalShippingOutlinedIcon,
  ConfirmationNumberOutlined as ConfirmationNumberOutlinedIcon,
  FileUploadOutlined as FileUploadOutlinedIcon,
  LogoutOutlined as LogoutOutlinedIcon,
  Insights as InsightsIcon,
  BarChart as BarChartIcon,
  AccountBalance as AccountBalanceIcon,
  Psychology as PsychologyIcon,
  NotificationsOutlined as NotificationsOutlinedIcon,
  PaymentOutlined as PaymentOutlinedIcon,
  EventNoteOutlined as BookingOutlinedIcon,
} from '@mui/icons-material';
import {
  Box,
  Typography,
  Drawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import Logo from '../../assets/swift_carry_logo.jpg';
import { STORAGE_KEYS } from '../../constants/storageKeys';
import { tokens } from '../../theme';

const Sidebar = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEYS.TOKEN);
    sessionStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
    window.location.href = '/';
  };

  useEffect(() => {
    setIsMounted(true);
    if (isSmallScreen) {
      setIsCollapsed(true);
      setIsDrawerOpen(false);
    } else {
      setIsDrawerOpen(false);
    }
  }, [isSmallScreen]);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 600) {
        setIsCollapsed(true);
        setIsDrawerOpen(false);
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleDrawerToggle = () => {
    if (isSmallScreen) {
      setIsDrawerOpen(!isDrawerOpen);
    } else {
      setIsCollapsed(!isCollapsed);
    }
  };

  if (!isMounted) {
    return null;
  }

  return (
    <>
      {/* Floating Mobile Menu Button */}
      {isSmallScreen && !isDrawerOpen && (
        <IconButton
          onClick={handleDrawerToggle}
          sx={{
            position: 'fixed',
            top: 16,
            left: 16,
            zIndex: 1400,
            backgroundColor: colors.swiftcarry[100],
            color: 'white',
            width: 56,
            height: 56,
            boxShadow: '0 4px 12px rgba(0, 201, 150, 0.4)',
            '&:hover': {
              backgroundColor: colors.swiftcarry[100],
              transform: 'scale(1.1)',
              boxShadow: '0 6px 16px rgba(0, 201, 150, 0.5)',
            },
            transition: 'all 0.3s ease',
          }}
        >
          <MenuOutlinedIcon sx={{ fontSize: '28px' }} />
        </IconButton>
      )}

      {/* Mobile overlay */}
      {isSmallScreen && isDrawerOpen && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 1200,
          }}
          onClick={() => setIsDrawerOpen(false)}
        />
      )}

      <Box display="flex">
        <Drawer
          variant={isSmallScreen ? 'temporary' : 'permanent'}
          open={!isSmallScreen || isDrawerOpen}
          onClose={() => setIsDrawerOpen(false)}
          ModalProps={{
            keepMounted: true,
            style: { zIndex: 1300 },
            disablePortal: false,
          }}
          PaperProps={{
            sx: {
              width: { xs: 280, sm: isCollapsed ? 80 : 250 },
              height: '100vh',
              position: { xs: 'fixed', sm: 'relative' },
              top: 0,
              left: 0,
              boxSizing: 'border-box',
              borderRight: '1px solid #e0e0e0',
              overflowX: 'hidden',
              overflowY: 'auto',
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
              background:
                'linear-gradient(180deg, #f8f9fa 0%, #ffffff 20%, #ffffff 80%, #f1f3f4 100%)',
              boxShadow: {
                xs: '2px 0 8px rgba(0,0,0,0.15)',
                sm: '0 2px 4px rgba(0,0,0,0.1)',
              },
              zIndex: { xs: 1300, sm: 'auto' },
            },
          }}
          sx={{
            width: { xs: 0, sm: isCollapsed ? 80 : 250 },
            flexShrink: 0,
            '& .MuiDrawer-paper': {
              width: { xs: 280, sm: isCollapsed ? 80 : 250 },
              transition: 'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            },
          }}
        >
          <Box
            display="flex"
            alignItems="center"
            justifyContent={{ xs: 'space-between', sm: 'center' }}
            width="100%"
            p={{ xs: '12px 16px', sm: 1 }}
            sx={{
              borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
              backgroundColor: { xs: '#f8f9fa', sm: 'transparent' },
            }}
          >
            <IconButton
              onClick={handleDrawerToggle}
              sx={{
                width: { xs: 44, sm: 40 },
                height: { xs: 44, sm: 40 },
                borderRadius: '50%',
                backgroundColor: {
                  xs: colors.swiftcarry[100],
                  sm: 'transparent',
                },
                color: { xs: 'white', sm: 'inherit' },
                '&:hover': {
                  backgroundColor: {
                    xs: colors.swiftcarry[100],
                    sm: 'rgba(0, 0, 0, 0.04)',
                  },
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease',
              }}
            >
              <MenuOutlinedIcon sx={{ fontSize: { xs: '24px', sm: '20px' } }} />
            </IconButton>

            {isSmallScreen && (
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 'bold',
                  color: colors.swiftcarry[100],
                  fontSize: '1.1rem',
                }}
              >
                SwiftCarry Admin
              </Typography>
            )}
          </Box>

          {(!isCollapsed || isSmallScreen) && (
            <Box textAlign="center" mb={2} sx={{ padding: '20px 16px' }}>
              <img
                alt="profile-user"
                width="80px"
                height="80px"
                src={Logo}
                style={{
                  cursor: 'pointer',
                  borderRadius: '50%',
                  border: '3px solid #00c996',
                  objectFit: 'cover',
                  display: 'block',
                  margin: '0 auto 12px',
                  boxShadow: '0 4px 12px rgba(0, 201, 150, 0.3)',
                }}
              />
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  fontSize: { xs: '1rem', sm: '1.25rem' },
                  color: '#333',
                  textShadow: '0 1px 2px rgba(0,0,0,0.1)',
                }}
              >
                Admin
              </Typography>
            </Box>
          )}

          <List
            sx={{
              padding: { xs: '8px 0', sm: '16px 0' },
              '& .MuiListItem-root': {
                minHeight: { xs: '44px', sm: '48px' },
                marginBottom: { xs: '4px', sm: '8px' },
                borderRadius: { xs: '0 24px 24px 0', sm: '8px' },
                marginRight: { xs: '8px', sm: '16px' },
                '&:hover': {
                  backgroundColor: 'rgba(0, 201, 150, 0.08)',
                  transform: 'translateX(4px)',
                  transition: 'all 0.2s ease',
                },
              },
            }}
          >
            <ListItem
              button
              component={Link}
              to="/dashboard"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <DashboardOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Dashboard"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/booking-intelligence"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <PsychologyIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Booking Intelligence"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              User Management
            </Typography>

            <ListItem
              button
              component={Link}
              to="/admin"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <PeopleOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Admins"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/customers"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ContactsOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Customers"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/role"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ReceiptOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Roles"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/manual-kyc"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ReceiptOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="KYC"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Booking Management
            </Typography>

            <ListItem
              button
              component={Link}
              to="/booking-dashboard"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <BarChartIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Booking Dashboard"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/booking"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <BookOnlineOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Booking"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Alerts
            </Typography>

            <ListItem
              button
              component={Link}
              to="/booking-alerts"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <BookingOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Booking Alerts"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/payment-alerts"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <PaymentOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Payment Alerts"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Promotions
            </Typography>

            <ListItem
              button
              component={Link}
              to="/promotions"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ConfirmationNumberOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Promotions"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Lookups
            </Typography>

            <ListItem
              button
              component={Link}
              to="/vehicletype"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <LocalShippingOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Vehicle Types"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/parcelweights"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ScaleOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Package Weights"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/packagetypes"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <InventoryOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Package Types"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Trip And Shipment
            </Typography>

            <ListItem
              button
              component={Link}
              to="/trips"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ConfirmationNumberOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Trip"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <ListItem
              button
              component={Link}
              to="/shipments"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <ConfirmationNumberOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Shipment"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Finance
            </Typography>

            <ListItem
              button
              component={Link}
              to="/finance"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <AccountBalanceIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Finance"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              Content Management
            </Typography>

            <ListItem
              button
              component={Link}
              to="/banners"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <FileUploadOutlinedIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="Banners"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Typography
              variant="body1"
              sx={{
                pl: { xs: 2, sm: isCollapsed ? 1 : 2 },
                mt: { xs: 2, sm: 1 },
                mb: 1,
                fontWeight: 'bold',
                fontSize: { xs: '14px', sm: isCollapsed ? '10px' : '16px' },
                textAlign: { xs: 'left', sm: isCollapsed ? 'center' : 'left' },
                borderBottom: { xs: '1px solid #e0e0e0', sm: 'none' },
                paddingBottom: { xs: 1, sm: 0 },
                color: '#666',
              }}
            >
              System
            </Typography>

            <ListItem
              button
              component={Link}
              to="/app-update"
              onClick={() => isSmallScreen && setIsDrawerOpen(false)}
            >
              <ListItemIcon
                sx={{
                  minWidth: '15px',
                  paddingRight: '10px',
                  paddingLeft: isCollapsed ? '18px' : '',
                  color: colors.swiftcarry[100],
                }}
              >
                <InsightsIcon />
              </ListItemIcon>
              {(!isCollapsed || isSmallScreen) && (
                <ListItemText
                  primary="App Update"
                  sx={{
                    '& .MuiListItemText-primary': {
                      fontSize: {
                        xs: '12px',
                        sm: isCollapsed ? '10px' : '14px',
                        md: isCollapsed ? '11px' : '15px',
                      },
                      color: 'rgba(0, 0, 0, 0.6)',
                    },
                  }}
                />
              )}
            </ListItem>

            <Box sx={{ mt: 2, borderTop: '1px solid #e0e0e0', pt: 2 }}>
              <ListItem button onClick={handleLogout}>
                <ListItemIcon
                  sx={{
                    minWidth: '15px',
                    paddingRight: '10px',
                    paddingLeft: isCollapsed ? '18px' : '',
                    color: '#dc3545',
                  }}
                >
                  <LogoutOutlinedIcon />
                </ListItemIcon>
                {(!isCollapsed || isSmallScreen) && (
                  <ListItemText
                    primary="Logout"
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontSize: {
                          xs: '12px',
                          sm: isCollapsed ? '10px' : '14px',
                          md: isCollapsed ? '11px' : '15px',
                        },
                        color: '#dc3545',
                        fontWeight: 500,
                      },
                    }}
                  />
                )}
              </ListItem>
            </Box>
          </List>
        </Drawer>
      </Box>
    </>
  );
};

export default Sidebar;
